import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaUserPlus } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import {
  validatePassword,
  validateEmail,
  validatePasswordMatch,
} from "../utils/passwordValidation";
import { queueEvent } from "../analytics";
import { EVENTS } from "../analytics/events";
import { Button, Field, Input, PasswordInput } from "../ui";
import AuthLayout, { AuthLink } from "../components/site/AuthLayout";
import { DiscountNote } from "../components/site/DiscountBanner";
import { authCarry, authDestination } from "../utils/authRedirect";

// Mirrors the backend isValidGameName: 1-24 chars, English letters/digits/dots/
// dashes only, no spaces, not starting/ending with a dot/dash, no consecutive dots.
const validateGameName = (name) => {
  if (!name) return { isValid: false, error: "اسم المستخدم مطلوب" };
  if (name.length < 1 || name.length > 24)
    return { isValid: false, error: "اسم المستخدم يجب أن يكون بين 1 و 24 حرفاً" };
  if (/^[.-]|[.-]$/.test(name))
    return { isValid: false, error: "لا يبدأ أو ينتهي بنقطة أو شرطة" };
  if (/\.\./.test(name))
    return { isValid: false, error: "لا يحتوي على نقطتين متتاليتين" };
  if (!/^[a-zA-Z0-9.-]+$/.test(name))
    return {
      isValid: false,
      error: "أحرف إنجليزية وأرقام ونقاط وشرطات فقط، بدون مسافات",
    };
  return { isValid: true, error: null };
};

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, isAuthenticated } = useAuth();
  const carry = authCarry(location);

  const [email, setEmail] = useState("");
  const [gameName, setGameName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailCheck = validateEmail(email.trim().toLowerCase());
    if (!emailCheck.isValid) {
      toast.error(emailCheck.error);
      return;
    }
    const nameCheck = validateGameName(gameName.trim());
    if (!nameCheck.isValid) {
      toast.error(nameCheck.error);
      return;
    }
    const passwordCheck = validatePassword(password);
    if (!passwordCheck.isValid) {
      passwordCheck.errors.forEach((err) => toast.error(err));
      return;
    }
    const matchCheck = validatePasswordMatch(password, confirmPassword);
    if (!matchCheck.isValid) {
      toast.error(matchCheck.error);
      return;
    }

    setLoading(true);
    try {
      await register({
        email: email.trim().toLowerCase(),
        password,
        gameName: gameName.trim().toLowerCase(),
      });
      queueEvent(EVENTS.AUTH_REGISTER_SUCCEEDED, { method: "email" });
      toast.success("تم إنشاء الحساب بنجاح");
      navigate(authDestination(location), { replace: true });
    } catch (error) {
      const message = error?.message || "";
      if (message === "Email or Game Name Already Exists!") {
        toast.error("البريد الإلكتروني أو اسم المستخدم مستخدم بالفعل");
      } else {
        toast.error(message || "حدث خطأ، حاول مرة أخرى");
      }
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated && !loading) {
    return <Navigate to={authDestination(location)} replace />;
  }

  return (
    <AuthLayout
      title="حساب جديد"
      subtitle="أنشئ حساباً لإنشاء ألعابك وإدارتها من المتصفح"
      footer={
        <>
          عندك حساب؟{" "}
          <AuthLink to="/login" state={carry}>
            تسجيل الدخول
          </AuthLink>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <DiscountNote />

        <Field label="البريد الإلكتروني" htmlFor="email">
          <Input
            ltr
            type="email"
            id="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@email.com"
            required
            disabled={loading}
          />
        </Field>

        <Field
          label="اسم المستخدم"
          htmlFor="gameName"
          hint="أحرف إنجليزية وأرقام فقط، بدون مسافات"
        >
          <Input
            ltr
            id="gameName"
            autoComplete="username"
            autoCapitalize="none"
            value={gameName}
            onChange={(e) => setGameName(e.target.value)}
            placeholder="username"
            required
            disabled={loading}
          />
        </Field>

        <Field label="كلمة المرور" htmlFor="password">
          <PasswordInput
            id="password"
            autoComplete="new-password"
            shown={showPassword}
            onToggle={() => setShowPassword((v) => !v)}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="8 أحرف على الأقل، حرف ورقم"
            required
            disabled={loading}
          />
        </Field>

        <Field label="تأكيد كلمة المرور" htmlFor="confirmPassword">
          <PasswordInput
            id="confirmPassword"
            autoComplete="new-password"
            shown={showPassword}
            onToggle={() => setShowPassword((v) => !v)}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="أعد كتابة كلمة المرور"
            required
            disabled={loading}
          />
        </Field>

        <Button type="submit" size="lg" block loading={loading} icon={<FaUserPlus />}>
          إنشاء حساب
        </Button>
      </form>
    </AuthLayout>
  );
};

export default Register;
